import { DRAW_TEMPLATES, type DrawTemplateKey } from '@/draw/core/templates';
import { isDrawPubliclyVisible } from '@/lib/appDrawProjection';
/**
 * Pure presentation rules for the public draw list. `format_type` and
 * `status` are free-text columns (no CHECK), so unknown values map to `null`
 * and the row simply omits that detail rather than showing a raw token.
 */
export type DrawFormatKey = 'knockout' | 'roundRobin' | 'poolPlayoffs' | 'poolsKnockout';
export type DrawStatusKey = 'inProgress' | 'published';

/** The parts of `draw.settings` that decide the label. */
export interface DrawPlan {
  template?: string | null;
  qualifying_pools?: { numPools?: number; poolDrawType?: string } | null;
}

/**
 * `format_type` is set once, from the template's name, so a draw's plan
 * outranks it. Only the standard templates do: an extra template's `drawType`
 * need not match its shape, so those keep the column. Two extras name their
 * own label: `futsal-show-ladder`, a round robin then a ladder with no pool
 * plan to read, and `pool-playoffs`, which no stored format or stage shape marks.
 */
export function drawFormatKey(
  formatType: string | null | undefined,
  plan?: DrawPlan | null,
): DrawFormatKey | null {
  const pools = plan?.qualifying_pools;
  const roundRobinPools = pools?.poolDrawType === 'round_robin' && (pools.numPools ?? 0) > 0;
  if (roundRobinPools && plan?.template !== 'round_robin') return 'poolsKnockout';
  if (plan?.template === 'futsal-show-ladder') return 'poolsKnockout';
  if (plan?.template === 'pool-playoffs') return 'poolPlayoffs';

  const standard = DRAW_TEMPLATES[plan?.template as DrawTemplateKey] as
    | { drawType: string }
    | undefined;
  switch (standard?.drawType ?? formatType) {
    case 'single_elimination':
    case 'monrad':
      return 'knockout';
    case 'round_robin':
      return 'roundRobin';
    case 'pool_playoffs':
      return 'poolPlayoffs';
    default:
      return null;
  }
}

export function drawStatusKey(status: string | null | undefined): DrawStatusKey | null {
  switch (status) {
    case 'active':
      return 'inProgress';
    case 'published':
      return 'published';
    default:
      return null;
  }
}

/** Public list rule: drafts never show; everything else is sorted by group order, then name. */
export function sortPublicDraws<
  T extends { name: string; status: string; event_group: { sort_order: number | null } },
>(draws: T[]): T[] {
  return draws
    .filter((draw) => isDrawPubliclyVisible(draw))
    .sort((a, b) => {
      const orderA = a.event_group.sort_order ?? 999;
      const orderB = b.event_group.sort_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });
}
